import { v4 as uuidv4 } from 'uuid';

class MetaPixelService {
  constructor() {
    // Prevent multiple service instances
    if (window.__metaPixelServiceInstance) {
      return window.__metaPixelServiceInstance;
    }

    this.isInitialized = false;
    this.pixelId = null;
    this.baseApiUrl = process.env.REACT_APP_URL || 'http://localhost:3000';

    // Global flag to prevent multiple Meta Pixel instances
    if (window.__metaPixelInitialized) {
      this.isInitialized = true;
      this.pixelId = window.__metaPixelId;
    }

    // Store this instance globally
    window.__metaPixelServiceInstance = this;
  }

  /**
   * Initialize Meta Pixel with environment-specific configuration
   */
  initialize() {
    // Multiple layers of protection against duplicate initialization

    // 1. Check if service is already initialized
    if (this.isInitialized) {
      return;
    }

    // 2. Check global flag to prevent multiple instances across the app
    if (window.__metaPixelInitialized) {
      this.isInitialized = true;
      this.pixelId = window.__metaPixelId;
      return;
    }

    // 3. Check if Facebook Pixel is already loaded and initialized
    if (window.fbq && window.fbq.loaded && window.fbq.version) {
      this.isInitialized = true;
      return;
    }

    // 4. Check if we're in the middle of initialization
    if (window.__metaPixelInitializing) {
      return;
    }

    // Set initialization flag to prevent concurrent initializations
    window.__metaPixelInitializing = true;

    // Get environment-specific Pixel ID based on REACT_APP_URL

    // Detect environment based on REACT_APP_URL
    // Treat localhost and staging as staging environment
    const isStaging =
      process.env.REACT_APP_URL &&
      (process.env.REACT_APP_URL.includes('staging') ||
        process.env.REACT_APP_URL.includes('localhost'));

    const pixelId = isStaging
      ? process.env.REACT_APP_META_PIXEL_ID_STAGING || '24054315874260873'
      : process.env.REACT_APP_META_PIXEL_ID_PRODUCTION || '1805006363351321';

    // Set the pixel ID
    this.pixelId = pixelId;

    if (!this.pixelId) {
      const detectedEnv =
        process.env.REACT_APP_URL &&
        (process.env.REACT_APP_URL.includes('staging') ||
          process.env.REACT_APP_URL.includes('localhost'))
          ? 'staging'
          : 'production';
      console.error(
        `Meta Pixel ID is missing for detected environment: ${detectedEnv}`,
      );
      console.error('Available environment variables:', {
        REACT_APP_URL: process.env.REACT_APP_URL,
        detectedEnvironment: detectedEnv,
        REACT_APP_META_PIXEL_ID_STAGING:
          process.env.REACT_APP_META_PIXEL_ID_STAGING,
        REACT_APP_META_PIXEL_ID_PRODUCTION:
          process.env.REACT_APP_META_PIXEL_ID_PRODUCTION,
      });

      // Use fallback pixel ID if no environment variable is set
      if (isStaging) {
        console.warn('Using fallback staging pixel ID: 24054315874260873');
        this.pixelId = '24054315874260873';
      } else {
        console.warn('Using fallback production pixel ID: 1805006363351321');
        this.pixelId = '1805006363351321';
      }
    }

    // Initialize Facebook Pixel script
    this.loadPixelScript();

    // Initialize pixel
    window.fbq('init', this.pixelId);

    // Only track initial PageView if not already tracked
    if (!window.__initialPageViewTracked) {
      window.fbq('track', 'PageView');
      window.__initialPageViewTracked = true;
    }

    this.isInitialized = true;

    // Set global flags to prevent multiple instances
    window.__metaPixelInitialized = true;
    window.__metaPixelId = this.pixelId;

    // Clear initialization flag
    window.__metaPixelInitializing = false;
  }

  /**
   * Load Facebook Pixel script
   */
  loadPixelScript() {
    // Multiple safety checks to prevent duplicate script loading
    if (window.fbq && window.fbq.loaded) {
      return;
    }

    if (document.querySelector('script[src*="fbevents.js"]')) {
      return;
    }

    // Additional check for global flag
    if (window.__metaPixelInitialized) {
      return;
    }

    !(function (f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod
          ? n.callMethod.apply(n, arguments)
          : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = '2.0';
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(
      window,
      document,
      'script',
      'https://connect.facebook.net/en_US/fbevents.js',
    );
  }

  /**
   * Track CompleteRegistration event (trial start)
   */
  async trackCompleteRegistration(userEmail, userPhone = null) {
    if (!this.isInitialized || !window.fbq) {
      console.error('Meta Pixel not initialized');
      return false;
    }

    // Prevent duplicate CompleteRegistration events for the same user
    const registrationEventKey = `completeRegistration_${userEmail}`;

    if (
      window.__trackedRegistrationEvents &&
      window.__trackedRegistrationEvents[registrationEventKey]
    ) {
      return true; // Return true to indicate "success" but don't track again
    }

    // Check if event is currently in progress to prevent race conditions
    if (this.isEventInProgress('CompleteRegistration', userEmail)) {
      return true; // Return true to indicate "success" but don't track again
    }

    try {
      // Generate unique event ID for deduplication
      const eventId = uuidv4();

      // Get Facebook cookies
      const fbp = this.getCookie('_fbp');
      const fbc = this.getCookie('_fbc');

      // Fire Pixel event (only if not already tracked)
      if (
        !window.__fbqCompleteRegistrationTracked ||
        !window.__fbqCompleteRegistrationTracked[registrationEventKey]
      ) {
        // Mark as tracked IMMEDIATELY to prevent race conditions
        if (!window.__fbqCompleteRegistrationTracked) {
          window.__fbqCompleteRegistrationTracked = {};
        }
        window.__fbqCompleteRegistrationTracked[registrationEventKey] = {
          eventId,
          timestamp: Date.now(),
          status: 'tracking',
        };

        window.fbq(
          'track',
          'CompleteRegistration',
          {},
          {
            eventID: eventId,
          },
        );
      } else {
        return true; // Exit early if already tracked
      }

      // Send data to backend for CAPI (only if Facebook Pixel event was tracked)
      if (
        window.__fbqCompleteRegistrationTracked &&
        window.__fbqCompleteRegistrationTracked[registrationEventKey]
      ) {
        const capiData = {
          event_id: eventId,
          event_name: 'CompleteRegistration',
          fbp: fbp,
          fbc: fbc,
          email: userEmail,
          phone: userPhone,
          event_source_url: window.location.href,
          user_agent: navigator.userAgent,
          timestamp: Math.floor(Date.now() / 1000),
        };

        try {
          await this.sendToBackend(
            '/api/v1/meta_pixel/conversion_events',
            capiData,
          );

          // Mark backend call as completed
          window.__fbqCompleteRegistrationTracked[registrationEventKey].status =
            'completed';
          window.__fbqCompleteRegistrationTracked[
            registrationEventKey
          ].backendCompleted = true;
        } catch (error) {
          // Mark backend call as failed
          window.__fbqCompleteRegistrationTracked[registrationEventKey].status =
            'failed';
          window.__fbqCompleteRegistrationTracked[registrationEventKey].error =
            error.message;

          console.error('CompleteRegistration backend API call failed:', error);
          throw error; // Re-throw to maintain error handling
        }
      }

      // Mark this registration event as tracked to prevent duplicates
      if (!window.__trackedRegistrationEvents) {
        window.__trackedRegistrationEvents = {};
      }
      window.__trackedRegistrationEvents[registrationEventKey] = {
        eventId,
        timestamp: Date.now(),
        userEmail,
      };

      return true;
    } catch (error) {
      console.error('Error tracking CompleteRegistration:', error);
      return false;
    }
  }

  /**
   * Track StartTrial event (free trial activation)
   */
  async trackStartTrial(
    userEmail,
    userPhone = null,
    trialLength = 3,
    priceAmount = null,
  ) {
    if (!this.isInitialized || !window.fbq) {
      console.error('Meta Pixel not initialized');
      return false;
    }

    // Prevent duplicate StartTrial events for the same user
    const trialEventKey = `startTrial_${userEmail}_${trialLength}`;
    if (
      window.__trackedTrialEvents &&
      window.__trackedTrialEvents[trialEventKey]
    ) {
      return true; // Return true to indicate "success" but don't track again
    }

    // Check if event is currently in progress to prevent race conditions
    if (this.isEventInProgress('StartTrial', userEmail, trialLength)) {
      return true; // Return true to indicate "success" but don't track again
    }

    try {
      // Generate unique event ID for deduplication
      const eventId = uuidv4();

      // Get Facebook cookies
      const fbp = this.getCookie('_fbp');
      const fbc = this.getCookie('_fbc');

      // Fire Pixel event (only if not already tracked)
      if (
        !window.__fbqStartTrialTracked ||
        !window.__fbqStartTrialTracked[trialEventKey]
      ) {
        // Mark as tracked IMMEDIATELY to prevent race conditions
        if (!window.__fbqStartTrialTracked) {
          window.__fbqStartTrialTracked = {};
        }
        window.__fbqStartTrialTracked[trialEventKey] = {
          eventId,
          timestamp: Date.now(),
          status: 'tracking',
        };

        window.fbq(
          'track',
          'StartTrial',
          {
            value: 0, // No monetary value for trial
            currency: 'USD',
            predicted_ltv: priceAmount ? parseFloat(priceAmount) : 0,
          },
          {
            eventID: eventId,
          },
        );
      } else {
        return true; // Exit early if already tracked
      }

      // Send data to backend for CAPI (only if Facebook Pixel event was tracked)
      if (
        window.__fbqStartTrialTracked &&
        window.__fbqStartTrialTracked[trialEventKey]
      ) {
        const capiData = {
          event_id: eventId,
          event_name: 'StartTrial',
          fbp: fbp,
          fbc: fbc,
          email: userEmail,
          phone: userPhone,
          event_source_url: window.location.href,
          user_agent: navigator.userAgent,
          timestamp: Math.floor(Date.now() / 1000),
          trial_length: trialLength,
          stripe_price_amount: priceAmount
            ? Math.round(parseFloat(priceAmount) * 100)
            : null, // Convert to cents
        };

        try {
          await this.sendToBackend(
            '/api/v1/meta_pixel/conversion_events',
            capiData,
          );

          // Mark backend call as completed
          window.__fbqStartTrialTracked[trialEventKey].status = 'completed';
          window.__fbqStartTrialTracked[trialEventKey].backendCompleted = true;
        } catch (error) {
          // Mark backend call as failed
          window.__fbqStartTrialTracked[trialEventKey].status = 'failed';
          window.__fbqStartTrialTracked[trialEventKey].error = error.message;

          console.error('StartTrial backend API call failed:', error);
          throw error; // Re-throw to maintain error handling
        }
      }

      // Mark this trial event as tracked to prevent duplicates
      if (!window.__trackedTrialEvents) {
        window.__trackedTrialEvents = {};
      }
      window.__trackedTrialEvents[trialEventKey] = {
        eventId,
        timestamp: Date.now(),
        userEmail,
        trialLength,
      };

      return true;
    } catch (error) {
      console.error('Error tracking StartTrial:', error);
      return false;
    }
  }

  /**
   * Track PageView event
   */
  trackPageView() {
    if (!this.isInitialized || !window.fbq) return;

    // Prevent duplicate PageView tracking on the same URL
    const currentUrl = window.location.href;
    if (window.__lastPageViewUrl === currentUrl) {
      return;
    }

    window.fbq('track', 'PageView');
    window.__lastPageViewUrl = currentUrl;
  }

  /**
   * Get cookie value by name
   */
  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop().split(';').shift();
    }
    return null;
  }

  /**
   * Send data to backend
   */
  async sendToBackend(endpoint, data) {
    try {
      let token = localStorage.getItem('token');
      if (!token) {
        const userData = localStorage.getItem('user');
        if (userData) {
          try {
            const parsedUser = JSON.parse(userData);
            token = parsedUser.token;
          } catch (e) {}
        }
      }
      const response = await fetch(`${this.baseApiUrl}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          // Add auth headers if available (not required for CompleteRegistration)
          ...(token && {
            Authorization: `Bearer ${token}`,
          }),
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `HTTP error! status: ${response.status} - ${errorText}`,
        );
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error sending data to backend:', error);
      throw error;
    }
  }

  /**
   * Check if user has given consent (placeholder for GDPR compliance)
   */
  hasConsent() {
    // Implement your consent logic here
    // For now, assuming consent is given
    return true;
  }

  /**
   * Check if Meta Pixel is already loaded globally
   */
  isGloballyLoaded() {
    return !!(window.fbq && window.fbq.loaded && window.__metaPixelInitialized);
  }

  /**
   * Reset global state (useful for testing)
   */
  resetGlobalState() {
    window.__metaPixelInitialized = false;
    window.__metaPixelId = null;
    window.__metaPixelInitializing = false;
    window.__initialPageViewTracked = false;
    window.__lastPageViewUrl = null;
    window.__trackedTrialEvents = {};
    window.__trackedRegistrationEvents = {};
    window.__fbqStartTrialTracked = {};
    window.__fbqCompleteRegistrationTracked = {};
    this.isInitialized = false;
    this.pixelId = null;
  }

  /**
   * Get current state for debugging
   */
  getDebugState() {
    return {
      serviceInitialized: this.isInitialized,
      pixelId: this.pixelId,
      globalFlag: window.__metaPixelInitialized,
      globalId: window.__metaPixelId,
      initializingFlag: window.__metaPixelInitializing,
      fbqLoaded: !!(window.fbq && window.fbq.loaded),
      fbqVersion: window.fbq?.version,
      initialPageViewTracked: window.__initialPageViewTracked,
      lastPageViewUrl: window.__lastPageViewUrl,
      currentUrl: window.location.href,
      trackedTrialEvents: window.__trackedTrialEvents || {},
      trackedRegistrationEvents: window.__trackedRegistrationEvents || {},
      fbqStartTrialTracked: window.__fbqStartTrialTracked || {},
      fbqCompleteRegistrationTracked:
        window.__fbqCompleteRegistrationTracked || {},
    };
  }

  /**
   * Check if Meta Pixel is in a valid state
   */
  isValidState() {
    return (
      this.isInitialized &&
      window.__metaPixelInitialized &&
      window.fbq &&
      window.fbq.loaded &&
      !window.__metaPixelInitializing
    );
  }

  /**
   * Check if a specific event has already been tracked
   */
  isEventTracked(eventType, userEmail, additionalKey = '') {
    if (eventType === 'StartTrial') {
      const trialEventKey = `startTrial_${userEmail}_${additionalKey}`;
      return !!(
        window.__trackedTrialEvents &&
        window.__trackedTrialEvents[trialEventKey]
      );
    }

    if (eventType === 'CompleteRegistration') {
      const registrationEventKey = `completeRegistration_${userEmail}`;
      return !!(
        window.__trackedRegistrationEvents &&
        window.__trackedRegistrationEvents[registrationEventKey]
      );
    }

    return false;
  }

  /**
   * Check if a specific Facebook Pixel event has already been tracked
   */
  isFbqEventTracked(eventType, userEmail, additionalKey = '') {
    if (eventType === 'StartTrial') {
      const trialEventKey = `startTrial_${userEmail}_${additionalKey}`;
      return !!(
        window.__fbqStartTrialTracked &&
        window.__fbqStartTrialTracked[trialEventKey]
      );
    }

    if (eventType === 'CompleteRegistration') {
      const registrationEventKey = `completeRegistration_${userEmail}`;
      return !!(
        window.__fbqCompleteRegistrationTracked &&
        window.__fbqCompleteRegistrationTracked[registrationEventKey]
      );
    }

    return false;
  }

  /**
   * Check if a specific event is currently being tracked (in progress)
   */
  isEventInProgress(eventType, userEmail, additionalKey = '') {
    if (eventType === 'StartTrial') {
      const trialEventKey = `startTrial_${userEmail}_${additionalKey}`;
      return !!(
        window.__fbqStartTrialTracked &&
        window.__fbqStartTrialTracked[trialEventKey] &&
        window.__fbqStartTrialTracked[trialEventKey].status === 'tracking'
      );
    }

    if (eventType === 'CompleteRegistration') {
      const registrationEventKey = `completeRegistration_${userEmail}`;
      return !!(
        window.__fbqCompleteRegistrationTracked &&
        window.__fbqCompleteRegistrationTracked[registrationEventKey] &&
        window.__fbqCompleteRegistrationTracked[registrationEventKey].status ===
          'tracking'
      );
    }

    return false;
  }

  /**
   * Get detailed information about tracked events for debugging
   */
  getEventDetails(eventType, userEmail, additionalKey = '') {
    if (eventType === 'StartTrial') {
      const trialEventKey = `startTrial_${userEmail}_${additionalKey}`;
      return window.__fbqStartTrialTracked?.[trialEventKey] || null;
    }

    if (eventType === 'CompleteRegistration') {
      const registrationEventKey = `completeRegistration_${userEmail}`;
      return (
        window.__fbqCompleteRegistrationTracked?.[registrationEventKey] || null
      );
    }

    return null;
  }

  /**
   * Force reinitialize (useful for debugging or recovery)
   */
  forceReinitialize() {
    this.resetGlobalState();
    delete window.__metaPixelServiceInstance;
    this.initialize();
  }

  /**
   * Set fbclid parameter preservation for fbc cookie
   */
  preserveFbclid() {
    const urlParams = new URLSearchParams(window.location.search);
    const fbclid = urlParams.get('fbclid');

    if (fbclid && !this.getCookie('_fbc')) {
      // Create fbc cookie if fbclid is present and fbc doesn't exist
      const timestamp = Math.floor(Date.now() / 1000);
      const fbcValue = `fb.1.${timestamp}.${fbclid}`;

      // Set cookie for 90 days
      const expires = new Date();
      expires.setTime(expires.getTime() + 90 * 24 * 60 * 60 * 1000);
      document.cookie = `_fbc=${fbcValue}; expires=${expires.toUTCString()}; path=/; domain=${window.location.hostname}`;
    }
  }
}

// Create singleton instance
const metaPixelService = new MetaPixelService();

export default metaPixelService;
